/**
 * views/FiscalClassificationView.js
 * ----------------------------------------------------------------------------
 * Platform-admin page for mapping the four product catalogues onto URA's
 * standardised product list: one commodity code (`goodsCategoryId`) and one
 * unit of measure per product line.
 *
 * WHY THIS IS A PAGE OF ITS OWN AND NOT TWO FIELDS ON THE PRODUCT FORM.
 * A vendor knows their supply; they do not know URA's classification of it, and
 * a vendor guessing produces a catalogue of confident wrong answers that look
 * exactly like right ones. The code also decides whether URA treats the item as
 * GOODS, which must be registered and stocked in before it can be invoiced at
 * all, or a SERVICE, which skips both. A wrong guess there can stop a till.
 * So the field never appears on a vendor-facing form, and the backend answers
 * 403 FISCAL_CLASSIFICATION_ADMIN_REQUIRED to anyone who is not platform staff.
 *
 * THE PAGE IS BUILT AROUND WHAT IS MISSING, not around what is set. The default
 * filter is "Not yet mapped" and the header is a coverage bar, because the
 * useful question during a mapping exercise is "what is left".
 *
 * PICK, DO NOT TYPE (23 Oct 2026). The server holds URA's own lists (148,016
 * commodity codes and 535 units, fetched from URA by the submitter), so both
 * codes are chosen with the shared search picker the category and unit tables
 * already use (components/UraReferencePicker.js), and the server refuses a code
 * that is not on URA's list. When a server has no lists loaded, the picker
 * falls back to a typed code on its own.
 *
 * MAP ONCE, NOT PER PRODUCT. A product with no code of its own files under its
 * Lusuku category's commodity code and its unit's URA unit
 * (services/fiscal/uraCodeResolver.js). The server resolves every row that way
 * and says where each code came from, and "mapped" means what would file. So
 * the fastest route is to map categories and units once, under Categorization,
 * and to use this page for what they cannot answer: restaurant dishes, which
 * carry neither, and the odd product whose category is too broad.
 *
 * EVERY STATE OFFERS ITS NEXT STEP (23 Oct 2026). An unmapped row's buttons are
 * the steps the server named for it: "Map category Rice" and "Map unit Kg" open
 * a dialog that maps that Lusuku category or unit right here, through
 * PATCH /api/fiscal-classification/categories/:id and /units/:id, and say how
 * many products it now answers for; "Map product" opens the product's own
 * dialog. Each catalogue tile opens the products left to map in it.
 *
 * Every table row is numbered, the table is striped like every console table,
 * and the pager says which page you are on and how many there are.
 *
 * @see controllers/fiscalClassificationController.js
 * @see models/shared/productTaxFields.js
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Table } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  apiBulkSetProductClassification,
  apiFiscalClassificationCoverage,
  apiFiscalClassificationMeta,
  apiListProductsForClassification,
  apiSetCategoryUraCode,
  apiSetProductClassification,
  apiSetUnitUraCode,
} from "../api/index.js";
import { AppButton, AppDialog, AppLinkButton, AppSelect } from "../components/ui";
import Loading from "../components/Loading";
import TablePager from "../components/table/TablePager";
import { UraLeafPicker, UraUnitPicker } from "../components/UraReferencePicker";
import UraTaxCategoriesTable from "../components/fiscal/UraTaxCategoriesTable.js";
import TaxHelp from "../components/fiscal/taxHelp.js";
import { PAGE, Pill, TYPE, errorText } from "../components/fiscal/fiscalUi.js";

const STATUSES = [
  { key: "UNCLASSIFIED", label: "Not yet mapped" },
  { key: "CLASSIFIED", label: "Mapped" },
  { key: "ALL", label: "All products" },
];

const PAGE_SIZE = 50;

/** The product's own VAT treatment, in words, as a meta line under its name. */
const VAT_LABEL = { STANDARD: "standard", ZERO_RATED: "zero rated", EXEMPT: "exempt" };

const S = {
  index: { width: 48, color: "#8595a8", fontVariantNumeric: "tabular-nums" },
  check: { width: 36 },
  bar: { height: 8, borderRadius: 999, background: "#eef2f7", overflow: "hidden", margin: "6px 0 14px" },
  barFill: (pct) => ({
    width: `${pct}%`,
    height: "100%",
    background: pct === 100 ? "#047857" : "#083860",
    borderRadius: 999,
  }),
  tiles: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 10, marginBottom: 18 },
  tile: {
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 10,
    padding: "10px 12px",
    display: "flex",
    flexDirection: "column",
    gap: 4,
    alignItems: "flex-start",
  },
  tileValue: { ...PAGE.figure, marginTop: 2 },
  coverageHead: { ...TYPE.body, display: "flex", justifyContent: "space-between", color: "#0f172a" },
  actions: { display: "inline-flex", flexDirection: "column", alignItems: "flex-end", gap: 6 },
  tipActions: { display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 },
};

/**
 * A product row's next step, as the server decided it (`nextSteps`, from
 * controllers/fiscalClassificationController.js): map its category, map its
 * unit, or, when there is nothing to inherit from, map the product itself.
 * Each is a button that opens the dialog for that step on this page.
 */
const STEP_BUTTON = {
  MAP_CATEGORY: (step) => ({ icon: "fa fa-sitemap", label: step.categoryName ? `Map category ${step.categoryName}` : "Map its category" }),
  MAP_UNIT: (step) => ({ icon: "fa fa-ruler", label: step.unitName ? `Map unit ${step.unitName}` : "Map its unit" }),
  /* `fields` says which code only the product can answer for: both, or just
     the one its category or unit could not supply. */
  MAP_PRODUCT: (step) => {
    const fields = Array.isArray(step.fields) ? step.fields : [];
    if (fields.length === 1 && fields[0] === "unitOfMeasure") return { icon: "fa fa-pen", label: "Set unit on product" };
    if (fields.length === 1 && fields[0] === "goodsCategoryId") return { icon: "fa fa-pen", label: "Set code on product" };
    return { icon: "fa fa-pen", label: "Map product" };
  },
};

/**
 * Where a resolved code came from, in words. The server names the Lusuku
 * category or unit that answered (`resolved.fromName`).
 */
const SOURCE_TEXT = {
  PRODUCT: () => "Set on this product",
  CATEGORY: (name) => (name ? `From category ${name}` : "From its category"),
  UNIT: (name) => (name ? `From unit ${name}` : "From its unit"),
};

/** One code cell: URA's code and name, and where it came from; or "Not mapped". */
const CodeCell = ({ code, name, from, fromName }) =>
  code ? (
    <>
      <span style={PAGE.mono}>{code}</span>
      {name ? <span className="lk-table-meta">{name}</span> : null}
      {from && SOURCE_TEXT[from] ? (
        <span className="lk-table-meta" style={{ fontStyle: "italic" }}>{SOURCE_TEXT[from](fromName)}</span>
      ) : null}
    </>
  ) : (
    <span style={{ ...PAGE.muted, whiteSpace: "nowrap" }}>Not mapped</span>
  );

/** Both codes resolve, one does, or neither. One on its own is not a filable line. */
const StatusPill = ({ row }) => {
  const r = row.resolved || {};
  if (row.classified) return <Pill tone="good">MAPPED</Pill>;
  if (r.goodsCategoryId || r.unitOfMeasure) return <Pill tone="warn">PARTLY MAPPED</Pill>;
  return <Pill tone="neutral">NOT MAPPED</Pill>;
};

/** The picker's value for a product's OWN code, named when the server knows the name. */
const ownCommodity = (row) =>
  row?.goodsCategoryId
    ? {
        code: row.goodsCategoryId,
        name: row.resolved?.from?.category === "PRODUCT" ? row.resolved.goodsCategoryName : null,
      }
    : null;
const ownUnit = (row) =>
  row?.unitOfMeasure
    ? { code: row.unitOfMeasure, name: row.resolved?.from?.unit === "PRODUCT" ? row.resolved.unitOfMeasureName : null }
    : null;

/**
 * What the product would fall back to if its own code were left empty, for the
 * hint under each picker. Only an inherited code is a fallback; a product's own
 * code is what the picker is editing.
 */
const fallbackHint = (row, which) => {
  const r = row?.resolved || {};
  const named = (code, name) => [code, name].filter(Boolean).join(" ");
  if (which === "commodity") {
    if (r.from?.category === "CATEGORY") {
      const source = r.fromName?.category ? `category ${r.fromName.category}` : "its category";
      return `Leave empty to keep using the code from ${source}: ${named(r.goodsCategoryId, r.goodsCategoryName)}.`;
    }
    return "Nothing to inherit: no category of this product carries a URA code.";
  }
  if (r.from?.unit === "UNIT") {
    const source = r.fromName?.unit ? `unit ${r.fromName.unit}` : "its unit";
    return `Leave empty to keep using the URA unit from ${source}: ${named(r.unitOfMeasure, r.unitOfMeasureName)}.`;
  }
  return "Nothing to inherit: this product's unit carries no URA unit.";
};

const FiscalClassificationView = () => {
  const [meta, setMeta] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [pageCount, setPageCount] = useState(1);
  const [loading, setLoading] = useState(false);
  const [denied, setDenied] = useState(false);

  const [vendorType, setVendorType] = useState("RESTAURANT");
  const [status, setStatus] = useState("UNCLASSIFIED");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);

  const [selected, setSelected] = useState(() => new Set());
  const [editing, setEditing] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [form, setForm] = useState({ commodity: null, unit: null });
  const [saving, setSaving] = useState(false);
  /* A Lusuku category or unit being mapped from a row's step: { kind, id, name }. */
  const [refEditing, setRefEditing] = useState(null);
  const [refPick, setRefPick] = useState(null);

  const loadMeta = useCallback(async () => {
    try {
      const { data } = await apiFiscalClassificationMeta();
      setMeta(data);
    } catch (err) {
      if (err?.response?.status === 403) setDenied(true);
      else toast.error(errorText(err, "Could not load classification metadata."));
    }
  }, []);

  const loadCoverage = useCallback(async () => {
    try {
      const { data } = await apiFiscalClassificationCoverage();
      setCoverage(data);
    } catch (err) {
      if (err?.response?.status !== 403) toast.error(errorText(err, "Could not load coverage."));
    }
  }, []);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiListProductsForClassification({
        vendorType,
        status,
        q: query || undefined,
        page,
        limit: PAGE_SIZE,
      });
      const count = data?.total ?? 0;
      setRows(data?.rows ?? []);
      setTotal(count);
      setPageCount(data?.pageCount ?? Math.max(1, Math.ceil(count / PAGE_SIZE)));
      setSelected(new Set());
    } catch (err) {
      if (err?.response?.status === 403) setDenied(true);
      else toast.error(errorText(err, "Could not load products."));
    } finally {
      setLoading(false);
    }
  }, [vendorType, status, query, page]);

  useEffect(() => {
    loadMeta();
    loadCoverage();
  }, [loadMeta, loadCoverage]);

  useEffect(() => {
    if (!denied) loadRows();
  }, [loadRows, denied]);

  const refresh = () => Promise.all([loadRows(), loadCoverage()]);

  const openEditor = (row) => {
    setForm({ commodity: ownCommodity(row), unit: ownUnit(row) });
    setEditing(row);
  };

  const openBulk = () => {
    setForm({ commodity: null, unit: null });
    setBulkOpen(true);
  };

  const codes = () => ({
    goodsCategoryId: form.commodity?.code || "",
    unitOfMeasure: form.unit?.code || "",
  });

  const saveOne = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await apiSetProductClassification(editing.vendorType, editing._id, codes());
      toast.success(`${editing.name} updated.`);
      setEditing(null);
      await refresh();
    } catch (err) {
      toast.error(errorText(err, "Could not save the classification."));
    } finally {
      setSaving(false);
    }
  };

  const saveBulk = async () => {
    setSaving(true);
    try {
      const { data } = await apiBulkSetProductClassification({ vendorType, productIds: [...selected], ...codes() });
      toast.success(`${data?.modified ?? 0} product(s) updated.`);
      setBulkOpen(false);
      await refresh();
    } catch (err) {
      toast.error(errorText(err, "Could not save the classification."));
    } finally {
      setSaving(false);
    }
  };

  const openStep = (row, step) => {
    if (step.action === "MAP_CATEGORY") {
      setRefPick(null);
      setRefEditing({ kind: "category", id: step.categoryId, name: step.categoryName });
    } else if (step.action === "MAP_UNIT") {
      setRefPick(null);
      setRefEditing({ kind: "unit", id: step.unitId, name: step.unitName });
    } else {
      openEditor(row);
    }
  };

  const saveRef = async () => {
    if (!refEditing) return;
    setSaving(true);
    const code = refPick?.code || "";
    try {
      const { data } =
        refEditing.kind === "category"
          ? await apiSetCategoryUraCode(refEditing.id, code)
          : await apiSetUnitUraCode(refEditing.id, code);
      const products = Number(data?.products) || 0;
      toast.success(
        code
          ? `${refEditing.name || "Mapped"}: ${products} product${products === 1 ? "" : "s"} now file under ${code}.`
          : `${refEditing.name || "The mapping"} cleared.`,
      );
      setRefEditing(null);
      await refresh();
    } catch (err) {
      toast.error(errorText(err, "Could not save the mapping."));
    } finally {
      setSaving(false);
    }
  };

  const showCatalogue = (key) => {
    setVendorType(key);
    setStatus("UNCLASSIFIED");
    setPage(1);
  };

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allOnPage = rows.length > 0 && rows.every((r) => selected.has(r._id));
  const toggleAll = () => setSelected(allOnPage ? new Set() : new Set(rows.map((r) => r._id)));

  const catalogues = meta?.catalogues ?? [];
  const catalogueLabel = (key) => catalogues.find((c) => c.key === key)?.label || key;
  const totals = coverage?.totals;
  const mappedPercent = useMemo(() => {
    if (!totals || !totals.total) return 0;
    return Math.round((totals.classified / totals.total) * 100);
  }, [totals]);
  const listsLoaded = Boolean(meta?.uraCodeListAvailable);

  if (denied) {
    return (
      <div style={PAGE.page}>
        <div style={PAGE.card}>
          <div style={PAGE.cardBody}>
            <div className="lk-note lk-note--warning">
              <span>
                <strong>Platform administrators only.</strong> A product&apos;s URA classification is a Lusuku-side
                mapping onto a government list, not a vendor setting, so this page is restricted to platform staff.
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={PAGE.page}>
      <div style={PAGE.card}>
        <div style={PAGE.cardHeader}>
          <div>
            <h4 style={PAGE.cardTitle}>URA product classification<TaxHelp topic="classification" /></h4>
            <p style={PAGE.cardSubtitle}>
              Every line on an EFRIS invoice or receipt carries a URA commodity code and a unit of measure. Lusuku
              staff map them here against URA&apos;s own lists; the vendor never sets them.
            </p>
          </div>
          <AppButton variant="outline" icon="fa fa-sync" onClick={refresh} disabled={loading}>Refresh</AppButton>
        </div>

        <div style={PAGE.cardBody}>
          {meta ? (
            <div
              className={`lk-note ${listsLoaded ? "lk-note--info" : "lk-note--warning"}`}
              style={{ marginBottom: 12 }}
              data-testid="ura-lists-note"
            >
              <span>
                <strong>{listsLoaded ? "URA's lists are loaded." : "URA's lists are not loaded on this server."}</strong>{" "}
                {meta.note}
              </span>
            </div>
          ) : null}

          <div className="lk-note" style={{ marginBottom: 16, display: "block" }} data-testid="map-once-tip">
            <span>
              <strong>Map once, not per product.</strong> A product with no code of its own files under its
              category&apos;s commodity code and its unit&apos;s URA unit, so mapping a category or a unit clears
              every product that uses it. Each row below offers that step first. Restaurant dishes carry neither,
              so map them here, a page at a time with the select-all box.
            </span>
            <div style={S.tipActions}>
              <AppLinkButton size="sm" icon="fa fa-sitemap" to="/admin/category-table">Open Active Categories</AppLinkButton>
              <AppLinkButton size="sm" icon="fa fa-ruler" to="/admin/units">Open Active Units</AppLinkButton>
            </div>
          </div>

          {totals ? (
            <div data-testid="coverage">
              <div style={S.coverageHead}>
                <strong>Mapped across all catalogues<TaxHelp topic="mappedMeaning" /></strong>
                <span>
                  {totals.classified.toLocaleString()} of {totals.total.toLocaleString()} ({mappedPercent}%)
                </span>
              </div>
              <div style={S.bar} role="progressbar" aria-valuenow={mappedPercent} aria-valuemin={0} aria-valuemax={100}>
                <div style={S.barFill(mappedPercent)} />
              </div>
              <div style={S.tiles}>
                {(coverage?.catalogues ?? []).map((c) => (
                  <div key={c.key} style={S.tile} data-testid="coverage-tile">
                    <div style={PAGE.figureLabel}>{c.label}</div>
                    <div style={S.tileValue}>{c.unclassified.toLocaleString()} left</div>
                    <div style={PAGE.note}>of {c.total.toLocaleString()} products</div>
                    <AppButton
                      size="sm"
                      icon="fa fa-filter"
                      disabled={c.unclassified === 0}
                      onClick={() => showCatalogue(c.key)}
                    >
                      {c.unclassified === 0 ? "All mapped" : `Show ${c.unclassified.toLocaleString()} to map`}
                    </AppButton>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          <div style={PAGE.toolbar}>
            <div className="lk-field" style={{ marginBottom: 0, minWidth: 170 }}>
              <label className="lk-field__label" htmlFor="fc-catalogue">Catalogue</label>
              <AppSelect
                id="fc-catalogue"
                block
                value={vendorType}
                onChange={(e) => { setVendorType(e.target.value); setPage(1); }}
              >
                {catalogues.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </AppSelect>
            </div>
            <div className="lk-field" style={{ marginBottom: 0, minWidth: 170 }}>
              <label className="lk-field__label" htmlFor="fc-status">Show</label>
              <AppSelect
                id="fc-status"
                block
                value={status}
                onChange={(e) => { setStatus(e.target.value); setPage(1); }}
              >
                {STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </AppSelect>
            </div>
            <div className="lk-field" style={{ marginBottom: 0, flex: "1 1 220px" }}>
              <label className="lk-field__label" htmlFor="fc-q">Search by product name</label>
              <input
                id="fc-q"
                className="lk-input"
                value={query}
                placeholder="e.g. rice"
                onChange={(e) => { setQuery(e.target.value); setPage(1); }}
              />
            </div>
            <AppButton icon="fa fa-tags" disabled={selected.size === 0} onClick={openBulk} data-testid="map-selected">
              {selected.size ? `Map ${selected.size} selected` : "Map selected"}
            </AppButton>
          </div>

          {loading ? <Loading size="panel" label="Loading products..." /> : null}

          {!loading ? (
            <div className="lk-table-frame">
              <Table responsive hover className="lk-table">
                <thead>
                  <tr>
                    <th style={S.check}>
                      <input
                        type="checkbox"
                        aria-label="Select every product on this page"
                        checked={allOnPage}
                        disabled={rows.length === 0}
                        onChange={toggleAll}
                      />
                    </th>
                    <th style={S.index}>#</th>
                    <th style={{ minWidth: 200 }}>Product</th>
                    <th>URA commodity code<TaxHelp topic="commodityCode" /></th>
                    <th>URA unit<TaxHelp topic="uraUnit" /></th>
                    <th>Status<TaxHelp topic="mappedMeaning" /></th>
                    <th className="lk-table-sticky-end" aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={7} style={PAGE.empty}>
                        {status === "UNCLASSIFIED" ? "Nothing left to map in this catalogue." : "No products match."}
                      </td>
                    </tr>
                  ) : null}
                  {rows.map((row, i) => {
                    const r = row.resolved || {};
                    return (
                      <tr key={`${row.vendorType}-${row._id}`} data-testid="product-row">
                        <td style={S.check}>
                          <input
                            type="checkbox"
                            aria-label={`Select ${row.name}`}
                            checked={selected.has(row._id)}
                            onChange={() => toggle(row._id)}
                          />
                        </td>
                        <td style={S.index} data-testid="row-index">{(page - 1) * PAGE_SIZE + i + 1}</td>
                        <td style={{ minWidth: 200 }}>
                          <span style={PAGE.strong}>{row.name}</span>
                          <span className="lk-table-meta">
                            {[`VAT ${VAT_LABEL[row.vatCategory] || VAT_LABEL.STANDARD}`, row.enabled === false ? "Switched off" : null]
                              .filter(Boolean)
                              .join(", ")}
                          </span>
                        </td>
                        <td data-testid="commodity-cell">
                          <CodeCell
                            code={r.goodsCategoryId}
                            name={r.goodsCategoryName}
                            from={r.from?.category}
                            fromName={r.fromName?.category}
                          />
                        </td>
                        <td data-testid="unit-cell">
                          <CodeCell
                            code={r.unitOfMeasure}
                            name={r.unitOfMeasureName}
                            from={r.from?.unit}
                            fromName={r.fromName?.unit}
                          />
                        </td>
                        <td><StatusPill row={row} /></td>
                        <td className="lk-table-sticky-end" style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                          <div style={S.actions} data-testid="row-actions">
                            {row.classified || !(row.nextSteps || []).length ? (
                              <AppButton size="sm" icon="fa fa-pen" onClick={() => openEditor(row)}>
                                {row.classified ? "Edit" : "Map product"}
                              </AppButton>
                            ) : (
                              row.nextSteps.map((step) => {
                                const b = (STEP_BUTTON[step.action] || STEP_BUTTON.MAP_PRODUCT)(step);
                                return (
                                  <AppButton
                                    key={`${step.action}-${step.categoryId || step.unitId || ""}`}
                                    size="sm"
                                    icon={b.icon}
                                    onClick={() => openStep(row, step)}
                                  >
                                    {b.label}
                                  </AppButton>
                                );
                              })
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
              <TablePager
                page={page}
                pageCount={pageCount}
                totalItems={total}
                pageSize={PAGE_SIZE}
                itemNoun="products"
                onChange={setPage}
              />
            </div>
          ) : null}
        </div>
      </div>

      {meta?.taxCategories?.length ? (
        <div style={PAGE.card}>
          <div style={PAGE.cardHeader}>
            <div>
              <h4 style={PAGE.cardTitle}>URA tax categories<TaxHelp topic="taxCategories" /></h4>
              <p style={PAGE.cardSubtitle}>
                Every tax category code URA defines for an e-invoice or e-receipt line, and which product VAT
                category on Lusuku files under each. A product&apos;s VAT category is set on the product itself; this
                table is for reference.
              </p>
            </div>
          </div>
          <div style={PAGE.cardBody}>
            <UraTaxCategoriesTable categories={meta.taxCategories} />
          </div>
        </div>
      ) : null}

      <AppDialog
        open={Boolean(editing)}
        onClose={() => !saving && setEditing(null)}
        busy={saving}
        title={editing?.name || ""}
        subtitle={editing ? `${catalogueLabel(editing.vendorType)} product` : ""}
        footer={
          <>
            <AppButton variant="quiet" onClick={() => setEditing(null)} disabled={saving}>Cancel</AppButton>
            <AppButton onClick={saveOne} loading={saving}>Save</AppButton>
          </>
        }
      >
        {editing ? (
          <>
            <div className="lk-field">
              <label className="lk-field__label" htmlFor="fc-commodity">URA commodity code<TaxHelp topic="commodityCode" /></label>
              <UraLeafPicker
                inputId="fc-commodity"
                value={form.commodity}
                onChange={(item) => setForm((f) => ({ ...f, commodity: item }))}
                isDisabled={saving}
              />
              <span className="lk-field__hint">{fallbackHint(editing, "commodity")}</span>
            </div>
            <div className="lk-field">
              <label className="lk-field__label" htmlFor="fc-unit">URA unit of measure<TaxHelp topic="uraUnit" /></label>
              <UraUnitPicker
                inputId="fc-unit"
                value={form.unit}
                onChange={(item) => setForm((f) => ({ ...f, unit: item }))}
                isDisabled={saving}
              />
              <span className="lk-field__hint">{fallbackHint(editing, "unit")}</span>
            </div>
            <p style={{ ...PAGE.note, margin: 0 }}>
              Clearing a code puts the product back in the not-yet-mapped queue unless its category or unit answers
              for it. That is the right thing to do with a code you are unsure of: a blank is an omission we can find
              again, a wrong code is a misdeclaration nobody will.
            </p>
          </>
        ) : null}
      </AppDialog>

      <AppDialog
        open={Boolean(refEditing)}
        onClose={() => !saving && setRefEditing(null)}
        busy={saving}
        title={
          refEditing
            ? `Map ${refEditing.kind} ${refEditing.name || ""}`.trim()
            : ""
        }
        subtitle={refEditing?.kind === "category" ? "Lusuku category" : "Lusuku unit"}
        footer={
          <>
            <AppButton variant="quiet" onClick={() => setRefEditing(null)} disabled={saving}>Cancel</AppButton>
            <AppButton onClick={saveRef} loading={saving} disabled={!refPick}>Save</AppButton>
          </>
        }
      >
        {refEditing ? (
          <>
            <div className="lk-note lk-note--info" style={{ marginBottom: 14 }}>
              <span>
                Every product {refEditing.kind === "category" ? "in" : "sold in"}{" "}
                <strong>{refEditing.name || `this ${refEditing.kind}`}</strong> without a code of its own will file
                under the {refEditing.kind === "category" ? "commodity code" : "URA unit"} you pick. A product that
                carries its own code keeps it.
              </span>
            </div>
            <div className="lk-field" style={{ marginBottom: 0 }}>
              <label className="lk-field__label" htmlFor="fc-ref">
                {refEditing.kind === "category" ? "URA commodity code" : "URA unit of measure"}
              </label>
              {refEditing.kind === "category" ? (
                <UraLeafPicker inputId="fc-ref" value={refPick} onChange={setRefPick} isDisabled={saving} />
              ) : (
                <UraUnitPicker inputId="fc-ref" value={refPick} onChange={setRefPick} isDisabled={saving} />
              )}
            </div>
          </>
        ) : null}
      </AppDialog>

      <AppDialog
        open={bulkOpen}
        onClose={() => !saving && setBulkOpen(false)}
        busy={saving}
        title={`Map ${selected.size} product${selected.size === 1 ? "" : "s"}`}
        subtitle={catalogueLabel(vendorType)}
        footer={
          <>
            <AppButton variant="quiet" onClick={() => setBulkOpen(false)} disabled={saving}>Cancel</AppButton>
            <AppButton onClick={saveBulk} loading={saving}>Apply to selected</AppButton>
          </>
        }
      >
        <div className="lk-note lk-note--warning" style={{ marginBottom: 14 }}>
          <span>
            The same pair of codes is written to every selected product. Only do this for products that really are
            the same kind of supply.
          </span>
        </div>
        <div className="lk-field">
          <label className="lk-field__label" htmlFor="fc-bulk-commodity">URA commodity code</label>
          <UraLeafPicker
            inputId="fc-bulk-commodity"
            value={form.commodity}
            onChange={(item) => setForm((f) => ({ ...f, commodity: item }))}
            isDisabled={saving}
          />
        </div>
        <div className="lk-field" style={{ marginBottom: 0 }}>
          <label className="lk-field__label" htmlFor="fc-bulk-unit">URA unit of measure</label>
          <UraUnitPicker
            inputId="fc-bulk-unit"
            value={form.unit}
            onChange={(item) => setForm((f) => ({ ...f, unit: item }))}
            isDisabled={saving}
          />
        </div>
      </AppDialog>
    </div>
  );
};

export default FiscalClassificationView;
